import { useEffect, useRef } from 'react';

/**
 * Closes a floating thing when the pointer goes down outside it, or on Escape.
 *
 * Spread the returned ref onto the element that should survive the click. The
 * listener is on `pointerdown` rather than `click` so a menu closes as soon as
 * the gesture starts, before whatever is underneath reacts to it - a menu that
 * is still open while you are already dragging something else feels broken.
 *
 * Registered in the capture phase, so it still fires when the element
 * underneath stops propagation for its own reasons.
 */
export function useDismiss<T extends HTMLElement>(active: boolean, onDismiss: () => void) {
  const ref = useRef<T | null>(null);
  // Held in a ref so the listeners can be attached once per open, rather than
  // torn down and rebuilt every time the caller re-renders with a new closure.
  // Updated in an effect rather than during render: a ref written while
  // rendering is state in disguise, and React is entitled to render twice.
  const handler = useRef(onDismiss);
  useEffect(() => {
    handler.current = onDismiss;
  });

  useEffect(() => {
    if (!active) return;

    function onPointerDown(event: PointerEvent) {
      const element = ref.current;
      if (element && event.target instanceof Node && !element.contains(event.target)) {
        handler.current();
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') handler.current();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [active]);

  return ref;
}
